import { useCallback } from 'react';
import useApi from './useApi';
import { useAppSettings } from '../hooks/useAppSettings';

export interface DraegerTubeUpdateResponse {
    success: boolean;
    data: any;
    message: string;
}

/**
 * Custom hook for updating draeger tube data
 * Uses POST/PUT endpoint: /UpsertDraegerTube
 */
const useUpdateDraegerTube = () => {
    const { state, postApiData } = useApi();
    const { settings: appSettings } = useAppSettings();

    /**
     * Updates a draeger tube via the UpsertDraegerTube API endpoint
     * @param tubeData - Complete draeger tube data
     * @returns Promise<DraegerTubeUpdateResponse>
     */
    const updateDraegerTube = useCallback(
        async (tubeData: any): Promise<DraegerTubeUpdateResponse> => {
            if (!tubeData || tubeData.id === undefined || tubeData.id === null) {
                throw new Error('Draeger tube data with valid ID is required');
            }

            try {
                const apiUrl = appSettings?.api;
                if (!apiUrl) {
                    throw new Error('API URL not configured in app settings');
                }

                const endpoint = `${apiUrl}UpsertDraegerTube`;

                // Convert camelCase to PascalCase for API
                const apiPayload = {
                    Id: tubeData.id,
                    OrderCode: tubeData.orderCode,
                    Description: tubeData.description,
                    IsNoDraegerTubeAvailable: tubeData.isNoDraegerTubeAvailable || false,
                    FilePath: tubeData.filePath || '',
                    LastUpdatedUtc: new Date().toISOString(),
                };

                const response = await postApiData({
                    endpoint,
                    data: apiPayload,
                    config: { params: { releaseId: appSettings?.releaseId || '' } },
                });

                return {
                    success: true,
                    data: response,
                    message: 'Draeger tube updated successfully',
                };
            } catch (error: any) {
                console.error('Failed to update draeger tube:', error);

                const errorMessage = error.response?.data?.message || error.response?.statusText || error.message || 'Failed to update draeger tube';

                throw new Error(errorMessage);
            }
        },
        [appSettings, postApiData]
    );

    return {
        state,
        updateDraegerTube,
        isLoading: state.isLoading,
        error: state.error,
        response: state.result as DraegerTubeUpdateResponse | null,
    };
};

export default useUpdateDraegerTube;
